import * as React from "react";
import { useNavigate } from "react-router-dom";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import {
  useCreateUserPointExchange,
  useGetCommunityPoint,
  useMyIdentities,
} from "@njoy/controller";
import { TemplateSub } from "../../../components/layouts/TemplateSub";
import { PointsBalanceCard } from "../../../components/cards/PointsBalanceCard";
import NewButton from "../../../components/uiMerge/newButton";
import { SuccessModal } from "../../../components/modals/sucessModal";
import { useBrand } from "../../../hooks/useBrand";
import ToastMessage from "../../../hooks/useToast";
import { extractError } from "../../../utils/extractError";
import Chevron from "../../../assets/svg/left-arrow.svg";
import * as ROUTES from "../../../constants/routes";

// Mirrors the server: rate * points, rounded down to 3 decimal places.
const toReceived = (rate: number, point: number) =>
  Math.floor(Number((rate * point * 1000).toFixed(6))) / 1000;

const formatPoints = (value: number) =>
  value.toLocaleString(undefined, { maximumFractionDigits: 3 });

const AmountVisibilityToggle: React.FC<{
  hidden: boolean;
  onToggle: () => void;
}> = ({ hidden, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={hidden ? "Show amounts" : "Hide amounts"}
    className="shrink-0 text-xl leading-none text-white/50"
  >
    {hidden ? <EyeInvisibleOutlined /> : <EyeOutlined />}
  </button>
);

export const ConvertPointsPage: React.FC = () => {
  const navigate = useNavigate();
  const { appName } = useBrand();
  const [selectedId, setSelectedId] = React.useState<string>();
  const [points, setPoints] = React.useState("");
  const [converted, setConverted] = React.useState(false);
  const [hidden, setHidden] = React.useState(false);
  const { createUserPointExchange, loading } = useCreateUserPointExchange();
  const { myIdentities, loading: identitiesLoading } = useMyIdentities({
    limit: 5,
    offset: 0,
  });

  // The member's communities, from their connected IDs.
  const communities = React.useMemo(() => {
    const byId = new Map<string, { id: string; alias: string }>();
    myIdentities.forEach(({ community }) => {
      if (community?.id && !byId.has(community.id)) {
        byId.set(community.id, {
          id: community.id,
          alias: community.alias || community.name,
        });
      }
    });
    return Array.from(byId.values());
  }, [myIdentities]);

  // Default to CBM, the only partner that supports conversion for now.
  const communityId =
    selectedId ??
    (communities.find((c) => c.alias === "CBM") ?? communities[0])?.id;
  const communityAlias =
    communities.find((c) => c.id === communityId)?.alias ?? "Partner";

  const {
    communityPoint,
    errors: communityPointErrors,
    loading: communityPointLoading,
    fetchCommunityPoint,
  } = useGetCommunityPoint(communityId);

  const balance = communityPoint?.memberCommunityPoint ?? undefined;
  const rate = communityPoint?.partnerCommunityExchangeRate ?? undefined;

  const amount = Number(points);
  const valid =
    Number.isInteger(amount) &&
    amount > 0 &&
    rate !== undefined &&
    balance !== undefined &&
    amount <= balance;
  const exceedsBalance = balance !== undefined && amount > balance;

  const submit = async () => {
    if (!communityId) return;
    const { errors } = await createUserPointExchange({
      communityId,
      point: amount,
    });
    if (errors) {
      ToastMessage("error", extractError(errors));
      return;
    }
    setConverted(true);
    setPoints("");
    fetchCommunityPoint();
  };

  const balanceText = communityPointLoading
    ? "..."
    : hidden
      ? "****"
      : balance !== undefined
        ? formatPoints(balance)
        : "--";

  const rateText = communityPointLoading
    ? `1 ${communityAlias} Point = ... ${appName} Points`
    : communityPointErrors
      ? extractError(communityPointErrors)
      : rate !== undefined
        ? `1 ${communityAlias} Point = ${formatPoints(rate)} ${appName} Points`
        : `1 ${communityAlias} Point = -- ${appName} Points`;

  const receivedText = hidden
    ? "****"
    : rate !== undefined && amount > 0 && Number.isFinite(amount)
      ? formatPoints(toReceived(rate, amount))
      : "--";

  return (
    <TemplateSub page="Convert Points">
      <div className="w-full flex-1 h-full overflow-y-auto px-[1rem] pb-7">
        <div className="mt-9">
          <PointsBalanceCard />
        </div>

        <p className="text-base font-light text-[#CECECF] mt-11">
          You can convert your points from other Partners into {appName} points.
        </p>

        <p className="text-sm text-white/50 mt-6">Amount we’ll convert</p>
        <div className="relative flex w-fit mt-[11px]">
          <select
            value={communityId ?? ""}
            onChange={(e) => {
              setSelectedId(e.target.value);
              setPoints("");
            }}
            disabled={communities.length === 0}
            aria-label="Community"
            className="appearance-none pl-6 pr-[45px] py-[10px] rounded-[20px] border border-white bg-transparent text-sm leading-[17px] text-white outline-none cursor-pointer"
          >
            {communities.length === 0 && (
              <option value="">
                {identitiesLoading ? "Loading..." : "No communities"}
              </option>
            )}
            {communities.map(({ id, alias }) => (
              <option key={id} value={id}>
                {alias}
              </option>
            ))}
          </select>
          <img
            src={Chevron}
            alt=""
            width={11}
            height={11}
            className="absolute right-6 top-1/2 -translate-y-1/2 -rotate-90 pointer-events-none"
          />
        </div>
        <div className="flex flex-col gap-[13px] h-[98px] px-[14px] pt-[31px] mt-[11px] bg-[#1E1E1E] rounded-lg">
          <div className="flex items-center gap-3">
            <input
              type={hidden ? "password" : "text"}
              inputMode="numeric"
              value={points}
              onChange={(e) => setPoints(e.target.value)}
              placeholder="0"
              aria-label={`${communityAlias} points to convert`}
              className="w-full min-w-0 bg-transparent text-lg font-black leading-[23px] text-white outline-none placeholder:!text-lg"
            />
            <AmountVisibilityToggle
              hidden={hidden}
              onToggle={() => setHidden(!hidden)}
            />
          </div>
          <p className="text-sm text-white/30">Bal: {balanceText} Points</p>
        </div>
        {exceedsBalance && (
          <p role="alert" className="text-sm text-[#FF165C] mt-[9px]">
            You don’t have enough {communityAlias} points.
            {!hidden &&
              ` You can convert up to ${formatPoints(balance)} points.`}
          </p>
        )}
        <p className="text-sm text-white/50 text-right mt-[9px]">{rateText}</p>

        <p className="text-sm text-white/50 mt-7">
          Your {appName} Points Balance Receives
        </p>
        <div className="w-fit mt-[10px] px-6 py-[10px] rounded-[20px] border border-white text-sm leading-[17px] text-white">
          {appName}
        </div>
        <div className="flex items-center gap-3 h-[98px] px-[14px] mt-[10px] bg-[#1E1E1E] rounded-lg">
          <p className="flex-1 text-lg font-black leading-[23px] text-white">
            {receivedText}
          </p>
          <AmountVisibilityToggle
            hidden={hidden}
            onToggle={() => setHidden(!hidden)}
          />
        </div>

        <NewButton
          fullWidth
          disabled={!valid || loading}
          onClick={submit}
          className="mt-[18px]"
        >
          {loading ? "Converting..." : "Convert Points"}
        </NewButton>
      </div>

      <SuccessModal
        visible={converted}
        onCancel={() => setConverted(false)}
        caption={`You have successfully converted your Points to ${appName} Points!`}
        buttonCaption="See Points Only Offers"
        onClick={() => navigate(ROUTES.DISCOVER)}
        closeButtonCaption="Close"
      />
    </TemplateSub>
  );
};
